/**
 * ClientPortal Component
 * Token-based entry point for client access to galleries and selection requests
 * Mobile-first with premium UX
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiLock, FiX, FiCheck } from 'react-icons/fi';

const API_BASE = '/api/portal';

// Password entry form
function PasswordPrompt({ onSubmit, error, loading }) {
  const [password, setPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(password);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiLock size={28} className="text-blue-500" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Password Required</h1>
            <p className="mt-2 text-sm text-gray-500">
              This link is password protected
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full px-4 py-3 text-base border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 text-center">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading || !password}
              className="w-full px-4 py-3 text-base font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50"
            >
              {loading ? 'Verifying...' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// Error display
function ErrorDisplay({ error, message }) {
  const errorConfig = {
    invalid: {
      icon: FiX,
      color: 'red',
      title: 'Invalid Link',
      description: 'This link is invalid or has been removed.'
    },
    revoked: {
      icon: FiX,
      color: 'orange',
      title: 'Link Revoked',
      description: 'This link has been revoked by the photographer.'
    },
    expired: {
      icon: FiX,
      color: 'gray',
      title: 'Link Expired',
      description: 'This link has expired. Please contact the photographer for a new link.'
    }
  };

  const config = errorConfig[error] || errorConfig.invalid;
  const Icon = config.icon;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="text-center">
        <div className={`w-20 h-20 bg-${config.color}-100 rounded-full flex items-center justify-center mx-auto mb-6`}>
          <Icon size={36} className={`text-${config.color}-500`} />
        </div>
        <h1 className="text-2xl font-semibold text-gray-900">{config.title}</h1>
        <p className="mt-3 text-gray-500 max-w-sm">
          {message || config.description}
        </p>
      </div>
    </div>
  );
}

// Loading spinner
function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="w-12 h-12 border-3 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="mt-4 text-gray-500">Loading...</p>
      </div>
    </div>
  );
}

// Main ClientPortal component
export default function ClientPortal() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [session, setSession] = useState(null);
  // Verify token on mount
  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async (password = null) => {
    setLoading(true);
    setPasswordError(null);

    try {
      const res = await fetch(`${API_BASE}/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });

      const data = await res.json();

      if (!res.ok) {
        if (data.error === 'password_required') {
          setNeedsPassword(true);
          setLoading(false);
          return;
        }

        if (data.error === 'invalid_password') {
          setPasswordError('Incorrect password');
          setLoading(false);
          return;
        }

        setError(data.error || 'invalid');
        setLoading(false);
        return;
      }

      // Store session token and navigate directly to gallery/selection
      sessionStorage.setItem('portal_session', data.session_token);
      setSession(data);
      setNeedsPassword(false);
      setLoading(false);

      // Navigate immediately, skip welcome interstitial
      if (data.type === 'selection_request') {
        navigate(`/s/${token}/select`);
      } else {
        navigate(`/g/${token}/gallery`);
      }
    } catch (err) {
      console.error('Token verification failed:', err);
      setError('invalid');
      setLoading(false);
    }
  };

  const handlePasswordSubmit = (password) => {
    verifyToken(password);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  if (needsPassword) {
    return (
      <PasswordPrompt
        onSubmit={handlePasswordSubmit}
        error={passwordError}
        loading={loading}
      />
    );
  }

  return null;
}

// Export session helper for other portal components
export function usePortalSession() {
  const sessionToken = sessionStorage.getItem('portal_session');

  return {
    sessionToken,
    headers: sessionToken ? {
      'Authorization': `Bearer ${sessionToken}`
    } : {}
  };
}
